import { ElementRef, Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { ImageCropData, LayerWithDomContextDto } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { CreativeUnitWithCustomizationsDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';
import { AdStudioContainerTabState } from './ad-studio-container/ad-studio-container.model';
import { AdEditorSidebarState, SidebarPropertyViewMode } from '../../../pages/sandbox/ad-editor/ad-editor-sidebar/ad-editor-sidebar.model';
import { CustomizationOptionDto } from "../../../../../../api/src/creative-unit-package/models/customization/customization-option.dto";
import { QueryParams } from "../collection/collection.model";
import { AdStudioSnappingSettings, ChangeHistoryEvent } from './ad-unit.model';

export interface AdStudioContainerState {
	appContext: 'editor' | 'preview' | 'review';
	previewContext: 'mockPreview' | 'preview'; // mock if we are in editor and toggle preview
	snappingState: AdStudioSnappingSettings;
	customizationsEditMode: boolean;

	/** Track whether and when the user has accepted the T&C (currently only used as fallback) */
	acceptedCreativePackageTermsDate: string;

	/** Track whether the user has completed the wizard (currently only used as fallback) */
	completedWizard: boolean;

	activeCreativeUnit: CreativeUnitWithCustomizationsDto;

	/** The ID of the active cart item group. */
	activeGroupId: string;

	/** The current merge tag state. */
	mergeTagState: { [key: string]: any };

	/** The currently selected creative unit merge state along with the active business. */
	activeCreativeUnitMergeState: { [key: string]: any };

	/** Whether the user is currently in select unit mode */
	/** If true, the user is in select unit mode and can select multiple units at once. */
	selectUnitMode: boolean;

	/** Tracks the currently selected units */
	selectedUnits: CreativeUnitWithCustomizationsDto['id'][];

	/** Store each change to use for undo/redo state. */
	changeHistoryState: {
		undoStack: ChangeHistoryEvent[];
		redoStack: ChangeHistoryEvent[];
		undoStackLimit: number;
		redoStackLimit: number;
		undoStackSize: number;
		redoStackSize: number;
		undoStackSizeLimit: number;
		redoStackSizeLimit: number;
	};

	interactions: {
		hoveredLayer: LayerWithDomContextDto;
		selectedLayers: LayerWithDomContextDto[];
		inlineEditedLayer?: LayerWithDomContextDto;
		isLassoActive?: boolean;
		enableInteractions: boolean; // Do any of the visible units include interactivity?
		templateIncludesInteractions: boolean; // When looking at the entirety of the template (including units, layers, customizations), does anything include interactivity?
		isMoving?: boolean;
		isResizing?: boolean;
		isPanning?: boolean;
	};

	customizations: {
		/** The customization's options and the query params we used to get it. */
		options: {
			[customizationId: string]: {
				options: CustomizationOptionDto[];
				queryParams: QueryParams;
			}
		}

		/**
		 * A cache of every past datasource call and the query parameters we used for it.
		 * The hash is made with the Datasource slug and the merged query params.
		 */
		optionCache: {
			[hash: string]: {
				options: CustomizationOptionDto[];
				loading: boolean;
			}
		}

		/** A map of currently pending fetch requests for customization options. */
		pendingFetchMap: { [hash: string]: Promise<CustomizationOptionDto[]> };
	}

	/** All of the preview area state */
	preview: {
		/** Sets the width of the preview area.  Allows users to make the sidebar bigger if needed. */
		previewWidth: number;

		/**
		 * The current scale of an item. Used for Zooming in.
		 * Keep in mind '1' is now functionally the same as 'fit'.
		*/
		previewScaleValue: string;

		/** The scale of an item after accounting for adjustments required to fit the container. */
		fitAdjustedPreviewScaleValue: string;

		/** The current pan position of the preview. */
		previewPan?: { x: number; y: number; };

		/** The maximum height of the preview. */
		previewMaxHeightValue: number;

		/** Should we show all items at once? */
		showAllItemsState: boolean;

		/** The list of all creative units for the package. */
		creativeUnitPreviews: CreativeUnitWithCustomizationsDto[];

		creativeUnitPreviewElementRefs: { [key: string]: ElementRef<any> };

		/** Extend the way to hide creative units from the "All Items" list. */
		hiddenCreativeUnits: string[];

		/** Create a way to hide creative units from the "All Items" list. */
		visibleCreativeUnitPreviews: CreativeUnitWithCustomizationsDto[];
		filteredCreativeUnits: string[];

		/** The width of the active item in pixels. */
		activeItemWidth: number;

		/** Whether the user is in crop box visual editing mode */
		cropBoxMode: boolean;
	};

	sidebar: AdStudioContainerSidebarState,

	/** Whether to show the extended animation timeline within the editor */
	showAnimationTimeline: boolean;

	/** The height of the animation timeline in px, set manually by the user via the resize handle */
	animationTimelineHeight: number | undefined;

	/** The width of the animation timeline layers panel in px, set manually by the user via the resize handle */
	animationTimelineLayersWidth: number | undefined;

	// TODO: The following two belong to the interaction state
	/** The ID of the unit preview we are actively interacting with */
	interactionUnitId: string;

	/** The ID of the group layer and its direct children we are interacting with (via dbl-click) */
	selectedGroupId: string;

	/** Seed value for cache-busting asset URLs */
	seed?: number;

	/** Crop mode state */
	cropMode: {
		active: boolean;
		layerId: string | null;
		creativeUnitId: string | null;
		initialCropData: ImageCropData | null;
	};

	/** Context for layer-linked and timestamp-linked comments */
	commentContext: {
		/** The ID of the layer selected for the current comment being composed */
		selectedLayerId: string | null;
		/** The timeline position (in seconds) captured when the comment input was focused */
		timelinePosition: number | null;
	};
}

export interface AdStudioContainerSidebarState {
	/** Controls the state of the sidebar area tabs */
	tabState: AdStudioContainerTabState;

	/** The state of the additional sidebars */
	sidebarState: AdEditorSidebarState;

	/**
	 * The expansion state of the customization tab expansion panels. (opened/closed/untouched)
	 * If a customization is undefined, it is considered untouched and will be opened based on the collapsedByDefault property.
	 */
	customizationExpandState: { [key: string]: boolean };

	// Expansion state for core parameter and options panels
	layerCoreParametersCollapsed? : boolean;
	layerOptionsCollapsed? : boolean;
	sidebarWidth?: number;
	propertyViewMode: SidebarPropertyViewMode


	/** Should we sync any changes across items, or just make changes for that one item? */
	syncAcrossPackageItems: boolean;
	showProperties: {
		type?: 'layers' | 'customizations',
		entityId?: string
	}

	/** The user's preferred animation editor mode ('preMade' | 'custom' | 'edit') */
	animationEditorMode: 'preMade' | 'custom' | 'edit';
}

export interface ZoomState {
	zoom: string;
	panX: number;
	panY: number;
	timestamp: number;
}

export function createInitialState(): AdStudioContainerState {
	return {
		previewContext :'preview',
		appContext: 'preview',
		snappingState: {
			edgeSnapping: true,
			layerSnapping: true
		},
		customizationsEditMode: false,
		acceptedCreativePackageTermsDate: null,
		completedWizard: null,
		activeCreativeUnit: null,
		activeGroupId: null,
		mergeTagState: {},
		activeCreativeUnitMergeState: {},
		selectedUnits: [],
		selectUnitMode: false,
		changeHistoryState: {
			undoStack: [],
			redoStack: [],
			undoStackLimit: 20,
			redoStackLimit: 20,
			undoStackSize: 0,
			redoStackSize: 0,
			undoStackSizeLimit: 20,
			redoStackSizeLimit: 20,
		},
		interactions: {
			hoveredLayer: null,
			selectedLayers: [],
			templateIncludesInteractions: true,
			enableInteractions: false,
			isMoving: false,
			isResizing: false,
			isPanning: false,
		},
		customizations: {
			options: {},
			optionCache: {},
			pendingFetchMap: {}
		},
		preview: {
			previewWidth: Number(localStorage.getItem('AdStudioEditorPreviewWidth')) || 75,
			previewScaleValue: '1',
			fitAdjustedPreviewScaleValue: '1',
			previewMaxHeightValue: 700,
			showAllItemsState: true,
			hiddenCreativeUnits: [],
			creativeUnitPreviews: [],
			creativeUnitPreviewElementRefs: {},
			visibleCreativeUnitPreviews: [],
			filteredCreativeUnits: [],
			activeItemWidth: 0,
			cropBoxMode: false
		},
		sidebar: {
			tabState: AdStudioContainerTabState.CUSTOMIZATIONS,
			sidebarState: new AdEditorSidebarState(),
			customizationExpandState: {},
			syncAcrossPackageItems: true,
			showProperties: {
				type: null,
				entityId: null
			},
			layerCoreParametersCollapsed: false,
			layerOptionsCollapsed: false,
			propertyViewMode: SidebarPropertyViewMode.BASIC,
			animationEditorMode: 'edit'
		},
		showAnimationTimeline: false,
		animationTimelineHeight: undefined,
		animationTimelineLayersWidth: 230,
		interactionUnitId: null,
		selectedGroupId: null,
		cropMode: null,
		commentContext: {
			selectedLayerId: null,
			timelinePosition: null
		}
	};
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'adStudioContainer', resettable: true })
export class AdStudioContainerStore extends Store<AdStudioContainerState> {
	constructor() {
		super(createInitialState());
	}
}
